import type { AccountingExpUpdate, ExpExactCandidate, ExpPoint, GameStateSnapshot } from '@/domain/types'
import {
  expMatchesRequirement,
  expRequirementRange,
  intersectExpRequirementRanges,
  representativeExpRequirement,
  requirementRangeContains,
  type ExpRequirementRange,
} from './exp-requirement-estimator'

type DisplayCandidateRisk = 'decrease' | 'requirement-conflict' | 'zero-baseline' | 'level-wrap'

interface PendingDisplayCandidate {
  readonly candidate: ExpExactCandidate
  readonly range: ExpRequirementRange | null
  readonly risk: DisplayCandidateRisk
}

export interface SessionExpDisplayStatus {
  readonly status: 'accepted' | 'pending' | 'rejected' | 'ignored'
  readonly candidateValue: number | null
  readonly acceptedValue: number | null
  readonly confirmedSamples: 0 | 1 | 2
  readonly targetSamples: 1 | 2
  readonly pendingSince: number | null
  readonly reason?: DisplayCandidateRisk | 'invalid' | 'stale' | 'missing-level-requirement'
}

export function exactSessionExpPoint(state: GameStateSnapshot | null): ExpPoint | null {
  if (!state
    || state.level === null
    || state.exp.absolute === null
    || state.exp.ratio === null
    || state.exp.estimated
    || !Number.isInteger(state.level)
    || !Number.isSafeInteger(state.exp.absolute)
    || state.exp.absolute < 0) {
    return null
  }

  const requiredExp = state.exp.requiredExp ?? null
  if (!expMatchesRequirement(state.exp, requiredExp)) return null
  return {
    level: state.level,
    absolute: state.exp.absolute,
    ratio: state.exp.ratio,
    requiredExp,
    requiredExpApproximate: state.exp.requiredExpApproximate ?? requiredExp !== null,
  }
}

/** Computes user-facing gain from one immutable start and the latest exact endpoint. */
export class SessionExpCounter {
  private startPoint: ExpPoint | null = null
  private endPoint: ExpPoint | null = null
  private endSourceAt: number | null = null
  private readonly requirements = new Map<number, { value: number; approximate: boolean }>()
  private pendingCandidate: PendingDisplayCandidate | null = null
  private displayStatus: SessionExpDisplayStatus = this.status('ignored')

  reset(initialState: GameStateSnapshot | null = null): void {
    this.requirements.clear()
    const point = exactSessionExpPoint(initialState)
    this.startPoint = point
    this.endPoint = point
    this.endSourceAt = point ? initialState?.observedAt?.exp ?? initialState?.capturedAt ?? null : null
    this.pendingCandidate = null
    this.displayStatus = this.status('ignored')
    if (point) this.rememberRequirement(point)
  }

  ingestCandidate(candidate: ExpExactCandidate | undefined): SessionExpDisplayStatus {
    if (!candidate) return this.displayStatus
    if (!this.validCandidate(candidate)) return this.setStatus('rejected', candidate, 0, 1, 'invalid')
    if (this.endSourceAt !== null && candidate.sourceAt <= this.endSourceAt) {
      return this.setStatus('ignored', candidate, 0, 1, 'stale')
    }
    if (!this.startPoint || !this.endPoint) return this.setStatus('rejected', candidate, 0, 1, 'invalid')

    const range = expRequirementRange(candidate.value, candidate.percentagePrecision)
    if (range === undefined) return this.setStatus('rejected', candidate, 0, 1, 'invalid')
    const risk = this.candidateRisk(candidate, range)
    if (risk) return this.confirmRiskyCandidate(candidate, range, risk)

    const point = this.pointFromCandidate(candidate, range)
    if (!point || !this.gainBetween(this.startPoint, point)) {
      return this.setStatus('rejected', candidate, 0, 1, 'missing-level-requirement')
    }
    this.acceptCandidate(point, candidate.sourceAt)
    return this.setStatus('accepted', candidate, 1, 1)
  }

  ingest(update: AccountingExpUpdate): void {
    if (!update.exact
      || !update.end
      || update.sourceEndAt === null
      || update.continuity === 'ignored'
      || update.continuity === 'pending'
      || update.continuity === 'unavailable'
      || (this.endSourceAt !== null && update.sourceEndAt <= this.endSourceAt)) {
      return
    }

    if (update.start) this.rememberRequirement(update.start)
    this.rememberRequirement(update.end)
    if (!this.startPoint) {
      this.startPoint = update.end
      this.endPoint = update.end
      this.endSourceAt = update.sourceEndAt
      return
    }

    this.endPoint = update.end
    this.endSourceAt = update.sourceEndAt
    this.pendingCandidate = null
    this.displayStatus = this.status('accepted')
  }

  endpointStatus(): SessionExpDisplayStatus {
    return this.displayStatus
  }

  total(): number | null {
    if (!this.startPoint || !this.endPoint) return null
    return this.gainBetween(this.startPoint, this.endPoint)?.value ?? null
  }

  approximate(): boolean {
    if (!this.startPoint || !this.endPoint) return false
    return this.gainBetween(this.startPoint, this.endPoint)?.approximate ?? false
  }

  latestSourceAt(): number | null {
    return this.endSourceAt
  }

  private gainBetween(start: ExpPoint, end: ExpPoint): { value: number; approximate: boolean } | null {
    if (end.level < start.level) return null
    if (end.level === start.level) return { value: end.absolute - start.absolute, approximate: false }

    const startRequirement = this.requirementFor(start.level, start)
    if (!startRequirement || start.absolute > startRequirement.value) return null
    let value = startRequirement.value - start.absolute
    let approximate = startRequirement.approximate
    for (let level = start.level + 1; level < end.level; level += 1) {
      const requirement = this.requirements.get(level)
      if (!requirement) return null
      value += requirement.value
      approximate ||= requirement.approximate
    }
    return { value: value + end.absolute, approximate }
  }

  private candidateRisk(
    candidate: ExpExactCandidate,
    range: ExpRequirementRange | null,
  ): DisplayCandidateRisk | null {
    const end = this.endPoint!
    if (candidate.level === end.level) {
      if (candidate.value.absolute < end.absolute) return 'decrease'
      const known = this.requirementFor(end.level, end)
      if (known && range && !requirementRangeContains(range, known.value)) return 'requirement-conflict'
      if (!known && end.absolute === 0 && end.ratio === 0) return 'zero-baseline'
      return null
    }
    if (candidate.level === end.level + 1) {
      const wrapped = candidate.value.absolute < end.absolute
        || candidate.value.ratio < end.ratio - 0.0002
      return wrapped ? null : 'level-wrap'
    }
    return 'level-wrap'
  }

  private confirmRiskyCandidate(
    candidate: ExpExactCandidate,
    range: ExpRequirementRange | null,
    risk: DisplayCandidateRisk,
  ): SessionExpDisplayStatus {
    const pending = this.pendingCandidate
    if (!pending || pending.risk !== risk || !this.riskyCandidatesAreCompatible(pending, candidate, range)) {
      this.pendingCandidate = { candidate, range, risk }
      return this.setStatus('pending', candidate, 1, 2, risk)
    }

    const mergedRange = pending.range && range
      ? intersectExpRequirementRanges([pending.range, range]) ?? null
      : range ?? pending.range
    const point = this.pointFromCandidate(candidate, mergedRange, risk === 'requirement-conflict')
    if (!point || !this.startPoint || !this.gainBetween(this.startPoint, point)) {
      this.pendingCandidate = null
      return this.setStatus('rejected', candidate, 0, 2, 'missing-level-requirement')
    }
    this.acceptCandidate(point, candidate.sourceAt)
    return this.setStatus('accepted', candidate, 2, 2)
  }

  private riskyCandidatesAreCompatible(
    pending: PendingDisplayCandidate,
    current: ExpExactCandidate,
    currentRange: ExpRequirementRange | null,
  ): boolean {
    const previous = pending.candidate
    if (current.sourceAt <= previous.sourceAt || current.level !== previous.level) return false
    if (current.value.absolute < previous.value.absolute) return false
    if (current.value.ratio < previous.value.ratio - 0.0002) return false
    if (pending.range === null || currentRange === null) return pending.range === currentRange
    return intersectExpRequirementRanges([pending.range, currentRange]) !== undefined
  }

  private pointFromCandidate(
    candidate: ExpExactCandidate,
    range: ExpRequirementRange | null,
    replaceRequirement = false,
  ): ExpPoint | null {
    if (candidate.level === null) return null
    const existing = replaceRequirement ? null : this.requirementFor(candidate.level)
    const inferred = range
      ? representativeExpRequirement(
          range,
          candidate.value.absolute,
          candidate.value.ratio > 0 ? candidate.value.absolute / candidate.value.ratio : null,
        )
      : null
    const requirement = existing ?? (inferred === null ? null : { value: inferred, approximate: true })
    if (requirement) this.requirements.set(candidate.level, requirement)
    return {
      level: candidate.level,
      absolute: candidate.value.absolute,
      ratio: candidate.value.ratio,
      requiredExp: requirement?.value ?? null,
      requiredExpApproximate: requirement?.approximate ?? false,
    }
  }

  private acceptCandidate(point: ExpPoint, sourceAt: number): void {
    this.rememberRequirement(point)
    this.endPoint = point
    this.endSourceAt = sourceAt
    this.pendingCandidate = null
  }

  private validCandidate(candidate: ExpExactCandidate): candidate is ExpExactCandidate & { level: number } {
    return candidate.level !== null
      && Number.isInteger(candidate.level)
      && candidate.level >= 1
      && candidate.level <= 200
      && Number.isSafeInteger(candidate.value.absolute)
      && candidate.value.absolute >= 0
      && Number.isFinite(candidate.value.ratio)
      && candidate.value.ratio >= 0
      && candidate.value.ratio <= 1
      && Number.isInteger(candidate.percentagePrecision)
      && candidate.percentagePrecision >= 0
      && candidate.percentagePrecision <= 4
      && Number.isFinite(candidate.sourceAt)
      && candidate.sourceAt <= candidate.deliveredAt
  }

  private setStatus(
    status: SessionExpDisplayStatus['status'],
    candidate: ExpExactCandidate,
    confirmedSamples: 0 | 1 | 2,
    targetSamples: 1 | 2,
    reason?: SessionExpDisplayStatus['reason'],
  ): SessionExpDisplayStatus {
    this.displayStatus = {
      status,
      candidateValue: candidate.value.absolute,
      acceptedValue: this.endPoint?.absolute ?? null,
      confirmedSamples,
      targetSamples,
      pendingSince: status === 'pending' ? this.pendingCandidate?.candidate.sourceAt ?? candidate.sourceAt : null,
      ...(reason ? { reason } : {}),
    }
    return this.displayStatus
  }

  private status(status: SessionExpDisplayStatus['status']): SessionExpDisplayStatus {
    return {
      status,
      candidateValue: null,
      acceptedValue: this.endPoint?.absolute ?? null,
      confirmedSamples: 0,
      targetSamples: 1,
      pendingSince: null,
    }
  }

  private requirementFor(level: number, point?: ExpPoint): { value: number; approximate: boolean } | null {
    if (point?.level === level && point.requiredExp !== null && point.requiredExp > 0) {
      return { value: point.requiredExp, approximate: point.requiredExpApproximate }
    }
    return this.requirements.get(level) ?? null
  }

  private rememberRequirement(point: ExpPoint): void {
    if (point.requiredExp === null || point.requiredExp <= 0) return
    this.requirements.set(point.level, {
      value: point.requiredExp,
      approximate: point.requiredExpApproximate,
    })
  }
}
